/**
 * Single source of truth for all popup colors (Game Over, Level Complete,
 * Score Submit, etc.). Lifted directly from the design reference (flat-cartoon
 * sky-blue + dark navy cards + pink stars + purple/yellow/green action buttons).
 *
 * Update once here and every overlay reflects the change.
 *
 * Reference image: bright cyan-blue panel, pink stars, navy stats card,
 * pale-blue subtitle pill with navy text, and three action buttons:
 *   - Home   → flat purple
 *   - Retry  → flat yellow (primary CTA, wider)
 *   - Next   → flat green
 */

export const OVERLAY_PALETTE = {
    backdrop:     'rgba(7,15,30,0.55)',

    panelBg:      '#338cde',
    panelBorder:  '#ffffff',
    panelBorderW: '6px',

    cardBg:       '#0d56a2',
    cardEdge:     '#0d56a2',

    titleColor:   '#ffffff',
    titleShadow:  'rgba(0,0,0,0.22)',
    starColor:    '#f288b4',

    labelColor:   '#cfe3f7',
    valueColor:   '#ffffff',
    valueShadow:  'rgba(0,0,0,0.30)',

    subtitleBg:   '#8ec2f6',
    subtitleEdge: '#8ec2f6',
    subtitleFg:   '#0d56a2',

    btnHomeBg:    '#744e98',
    btnHomeEdge:  '#523572',
    btnHomeFg:    '#ffffff',

    btnRetryBg:   '#fbdf34',
    btnRetryEdge: '#c89c0a',
    btnRetryFg:   '#3b2a07',

    btnNextBg:    '#78ac22',
    btnNextEdge:  '#4a6e0d',
    btnNextFg:    '#ffffff',

    accentScore:  '#fbdf34',
    accentGold:   '#fbdf34',

    inputBg:      '#0d56a2',
    inputEdge:    '#2273c4',
    inputFocus:   '#fbdf34',

    rowOdd:       '#1265b8',
    rowEven:      '#0d56a2',
    topRowBg:     'rgba(251,223,52,0.16)',
    topRowEdge:   'rgba(251,223,52,0.55)',

    okColor:      '#78ac22',
    errColor:     '#f87171',
};

/**
 * Build the shared `:root`-scoped CSS variables so other overlays can pull
 * exact colors via `var(--ov-panel-bg)` without duplicating the palette in
 * every stylesheet.
 *
 * Call once before rendering an overlay; subsequent calls are no-ops.
 */
let _varsInjected = false;
export function injectOverlayVars() {
    if (_varsInjected) return;
    _varsInjected = true;
    const css = `
        :root {
            --ov-backdrop: ${OVERLAY_PALETTE.backdrop};
            --ov-panel-bg: ${OVERLAY_PALETTE.panelBg};
            --ov-panel-border: ${OVERLAY_PALETTE.panelBorder};
            --ov-panel-border-width: ${OVERLAY_PALETTE.panelBorderW};
            --ov-card-bg: ${OVERLAY_PALETTE.cardBg};
            --ov-card-edge: ${OVERLAY_PALETTE.cardEdge};
            --ov-title: ${OVERLAY_PALETTE.titleColor};
            --ov-title-shadow: ${OVERLAY_PALETTE.titleShadow};
            --ov-star: ${OVERLAY_PALETTE.starColor};
            --ov-label: ${OVERLAY_PALETTE.labelColor};
            --ov-value: ${OVERLAY_PALETTE.valueColor};
            --ov-value-shadow: ${OVERLAY_PALETTE.valueShadow};
            --ov-subtitle-bg: ${OVERLAY_PALETTE.subtitleBg};
            --ov-subtitle-edge: ${OVERLAY_PALETTE.subtitleEdge};
            --ov-subtitle-fg: ${OVERLAY_PALETTE.subtitleFg};
            --ov-btn-home-bg: ${OVERLAY_PALETTE.btnHomeBg};
            --ov-btn-home-edge: ${OVERLAY_PALETTE.btnHomeEdge};
            --ov-btn-home-fg: ${OVERLAY_PALETTE.btnHomeFg};
            --ov-btn-retry-bg: ${OVERLAY_PALETTE.btnRetryBg};
            --ov-btn-retry-edge: ${OVERLAY_PALETTE.btnRetryEdge};
            --ov-btn-retry-fg: ${OVERLAY_PALETTE.btnRetryFg};
            --ov-btn-next-bg: ${OVERLAY_PALETTE.btnNextBg};
            --ov-btn-next-edge: ${OVERLAY_PALETTE.btnNextEdge};
            --ov-btn-next-fg: ${OVERLAY_PALETTE.btnNextFg};
            --ov-accent-score: ${OVERLAY_PALETTE.accentScore};
            --ov-accent-gold: ${OVERLAY_PALETTE.accentGold};
            --ov-input-bg: ${OVERLAY_PALETTE.inputBg};
            --ov-input-edge: ${OVERLAY_PALETTE.inputEdge};
            --ov-input-focus: ${OVERLAY_PALETTE.inputFocus};
            --ov-row-odd: ${OVERLAY_PALETTE.rowOdd};
            --ov-row-even: ${OVERLAY_PALETTE.rowEven};
            --ov-top-row-bg: ${OVERLAY_PALETTE.topRowBg};
            --ov-top-row-edge: ${OVERLAY_PALETTE.topRowEdge};
            --ov-ok: ${OVERLAY_PALETTE.okColor};
            --ov-err: ${OVERLAY_PALETTE.errColor};
            --ov-backdrop-blur: 10px;
        }

        /* Shared backdrop blur — every overlay root that uses the standard
           --ov-backdrop scrim also gets a 10px glass blur over the game
           behind it. Listed explicitly because each overlay has its own
           prefix; add new ones here when you ship them. */
        .gov-root,
        .lc-root,
        .ss-root,
        .hov-root,
        .lbov-root {
            backdrop-filter: blur(var(--ov-backdrop-blur));
            -webkit-backdrop-filter: blur(var(--ov-backdrop-blur));
        }
    `;
    const style = document.createElement('style');
    style.dataset.ovPalette = '1';
    style.textContent = css;
    document.head.appendChild(style);
}
